import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import ReportCardPreviewModal from '../components/ReportCardPreviewModal';
import ParentAssignmentsSection from './parent/ParentAssignmentsSection';
import ParentExtraClassesSection from './parent/ParentExtraClassesSection';
import ParentChat from '../components/communication/ParentChat';
import ParentHostelSection from './parent/HostelSection';
import ParentTransportSection from './parent/TransportSection';
import ParentLibrarySection from './parent/ParentLibrarySection';
import ParentSecurityPortalPage from './security/ParentSecurityPortalPage';
import ParentPTMSection from './parent/ParentPTMSection';
import ParentServicesSection from './parent/ParentServicesSection';
import TermlyFeeCard from '../components/TermlyFeeCard';
import { useAuth } from '../context/AuthContext';
import parentService from '../services/parentService';
import schoolsService from '../services/schoolsService';
import gradesService from '../services/gradesService';
import canteenService from '../services/canteenService';
import {
  Student, BookOpen, CurrencyCircleDollar, CheckCircle,
  Megaphone, ChartBar, CalendarBlank, Warning, Clock,
  CaretRight, Eye, FileText, ChatDots, MapPin, House, ShieldCheck, Wallet, QrCode, UsersThree
} from '@phosphor-icons/react';

// GES Grade colors
const GRADE_COLORS = {
  '1': '#059669', '2': '#10B981', '3': '#34D399',
  '4': '#FBBF24', '5': '#F59E0B', '6': '#F97316',
  '7': '#EF4444', '8': '#DC2626', '9': '#991B1B'
};

function formatCurrency(amount) {
  return `GHS ${amount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ParentPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('overview'); // overview, grades, fees, attendance, assignments, chat, security, canteen-wallet
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childOverview, setChildOverview] = useState(null);
  const [childGrades, setChildGrades] = useState(null);
  const [childFees, setChildFees] = useState(null);
  const [childAttendance, setChildAttendance] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [academicTerms, setAcademicTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [selectedAcademicTerm, setSelectedAcademicTerm] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHTML, setPreviewHTML] = useState('');
  const [walletSummary, setWalletSummary] = useState(null);
  const [walletHistory, setWalletHistory] = useState([]);
  const [walletControls, setWalletControls] = useState(null);
  const [childOrders, setChildOrders] = useState([]);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [savingControls, setSavingControls] = useState(false);
  // Optional modules enrollment for selected child
  const [childEnrollment, setChildEnrollment] = useState(null);
  const [childHostelData, setChildHostelData] = useState(null);
  const [childTransportData, setChildTransportData] = useState(null);

  // Check if user has required role
  useEffect(() => {
    if (user && user.role !== 'parent') {
      toast.error('You do not have access to this page');
      navigate('/');
    }
  }, [user, navigate]);

  // Load children on mount
  useEffect(() => {
    Promise.all([
      parentService.getChildren(),
      parentService.getAnnouncements(),
      schoolsService.listTerms(user?.school_id)
    ])
      .then(([childrenData, announcementsData, termsData]) => {
        setChildren(childrenData || []);
        setAnnouncements(announcementsData || []);
        setAcademicTerms(termsData || []);
        
        if (childrenData && childrenData.length > 0) {
          setSelectedChild(childrenData[0]);
        }
        
        // Set the first term as default
        if (termsData && termsData.length > 0) {
          setSelectedAcademicTerm(termsData[0].id);
        }
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [user?.school_id]);

  // Load child overview
  const loadChildOverview = useCallback(() => {
    if (!selectedChild) return;
    parentService.getChildOverview(selectedChild.id, selectedAcademicTerm)
      .then(data => setChildOverview(data))
      .catch(err => toast.error('Failed to load overview'));
  }, [selectedChild, selectedAcademicTerm]);

  // Load child grades
  const loadChildGrades = useCallback(() => {
    if (!selectedChild || !selectedAcademicTerm) return;
    parentService.getChildGrades(selectedChild.id, selectedAcademicTerm)
      .then(data => setChildGrades(data))
      .catch(err => toast.error('Failed to load grades'));
  }, [selectedChild, selectedAcademicTerm]);

  // Load child fees
  const loadChildFees = useCallback(() => {
    if (!selectedChild) return;
    // Allow loading fees without a term if no terms exist (backward compatibility)
    parentService.getChildFees(selectedChild.id, selectedAcademicTerm)
      .then(data => setChildFees(data))
      .catch(err => toast.error('Failed to load fees'));
  }, [selectedChild, selectedAcademicTerm]);

  // Handle successful payment
  const handlePaymentSuccess = useCallback(() => {
    toast.success('Payment initiated successfully! Please wait for confirmation.');
    // Reload fees after a short delay to show updated status
    setTimeout(() => {
      loadChildFees();
    }, 2000);
  }, [loadChildFees]);

  // Load child attendance
  const loadChildAttendance = useCallback(() => {
    if (!selectedChild) return;
    parentService.getChildAttendance(selectedChild.id, selectedAcademicTerm)
      .then(data => setChildAttendance(data))
      .catch(err => toast.error('Failed to load attendance'));
  }, [selectedChild, selectedAcademicTerm]);

  const loadWalletSummary = useCallback(() => {
    if (!selectedChild) return;
    canteenService.getParentSummary(selectedChild.id)
      .then(data => setWalletSummary(data))
      .catch(err => console.error('Failed to load wallet summary'));
  }, [selectedChild]);

  const loadWalletHistory = useCallback(() => {
    if (!selectedChild) return;
    canteenService.getParentHistory(selectedChild.id, 8)
      .then(data => setWalletHistory(Array.isArray(data) ? data : []))
      .catch(err => console.error('Failed to load wallet history'));
  }, [selectedChild]);

  const loadWalletControls = useCallback(() => {
    if (!selectedChild) return;
    canteenService.getWalletControls(selectedChild.id)
      .then(data => setWalletControls(data))
      .catch(err => console.error('Failed to load spending controls'));
  }, [selectedChild]);

  const loadChildOrders = useCallback(() => {
    if (!selectedChild) return;
    canteenService.listOrders({ studentId: selectedChild.id, limit: 5 })
      .then(data => setChildOrders(Array.isArray(data) ? data : []))
      .catch(err => console.error('Failed to load orders'));
  }, [selectedChild]);

  const handleTopUp = useCallback(async () => {
    if (!selectedChild?.id) {
      toast.error('Select a child first');
      return;
    }
    const amount = Number(topUpAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid top-up amount');
      return;
    }
    try {
      const response = await canteenService.topUpWallet({ studentId: selectedChild.id, amount });
      if (response.success && response.payment_url) {
        window.open(response.payment_url, '_blank', 'width=800,height=700');
        toast.success('Payment checkout opened');
        setTopUpAmount('');
      } else {
        toast.error(response.error || 'Failed to initialize payment');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to initialize payment');
    }
  }, [selectedChild, topUpAmount]);

  const toggleBlockedCategory = useCallback((category) => {
    setWalletControls(prev => {
      const current = prev?.blocked_categories || [];
      const next = current.includes(category)
        ? current.filter(c => c !== category)
        : [...current, category];
      return { ...prev, blocked_categories: next };
    });
  }, []);

  const handleSaveControls = useCallback(async () => {
    if (!selectedChild?.id || !walletControls) return;
    setSavingControls(true);
    try {
      const updated = await canteenService.updateWalletControls(selectedChild.id, {
        frozen: walletControls.frozen,
        daily_limit: walletControls.daily_limit ?? undefined,
        clear_daily_limit: walletControls.daily_limit === null || walletControls.daily_limit === '' || walletControls.daily_limit === undefined,
        weekly_limit: walletControls.weekly_limit ?? undefined,
        clear_weekly_limit: walletControls.weekly_limit === null || walletControls.weekly_limit === '' || walletControls.weekly_limit === undefined,
        blocked_categories: walletControls.blocked_categories || [],
        low_balance_threshold: walletControls.low_balance_threshold,
      });
      setWalletControls(updated);
      toast.success('Spending controls updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update spending controls');
    } finally {
      setSavingControls(false);
    }
  }, [selectedChild, walletControls]);

  // Load child enrollment status
  useEffect(() => {
    if (!selectedChild) return;
    parentService.getChildEnrollmentStatus(selectedChild.id)
      .then(data => setChildEnrollment(data))
      .catch(err => console.error('Failed to load enrollment status'));
  }, [selectedChild]);

  // Load child hostel data if enrolled
  useEffect(() => {
    if (!selectedChild || !childEnrollment?.has_hostel) return;
    parentService.getChildHostelStatus(selectedChild.id)
      .then(data => setChildHostelData(data))
      .catch(err => console.error('Not enrolled in hostel'));
  }, [selectedChild, childEnrollment?.has_hostel]);

  // Load child transport data if enrolled
  useEffect(() => {
    if (!selectedChild || !childEnrollment?.has_transport) return;
    parentService.getChildTransportStatus(selectedChild.id)
      .then(data => setChildTransportData(data))
      .catch(err => console.error('Not enrolled in transport'));
  }, [selectedChild, childEnrollment?.has_transport]);

  // Download report card as PDF
  // Preview report card as HTML modal
  const previewReportCard = useCallback(async () => {
    if (!selectedChild || !selectedChild.id) {
      toast.error('Child information not loaded');
      return;
    }
    
    setDownloadingPDF(true);
    try {
      const data = await gradesService.previewReportCard(selectedChild.id, selectedAcademicTerm);
      setPreviewHTML(data.html);
      setShowPreviewModal(true);
    } catch (error) {
      toast.info(error.message === 'Report card not yet available'
        ? 'Your child\'s report card is still being prepared by the school.'
        : 'We could not load the report card right now. Please try again later.');
    } finally {
      setDownloadingPDF(false);
    }
  }, [selectedChild, selectedAcademicTerm]);

  useEffect(() => {
    if (selectedChild) {
      loadChildOverview();
      if (view === 'grades') loadChildGrades();
      if (view === 'fees') loadChildFees();
      if (view === 'attendance') loadChildAttendance();
      if (view === 'canteen-wallet') {
        loadWalletSummary();
        loadWalletHistory();
        loadWalletControls();
        loadChildOrders();
      }
    }
  }, [selectedChild, view, selectedAcademicTerm, loadChildOverview, loadChildGrades, loadChildFees, loadChildAttendance, loadWalletSummary, loadWalletHistory, loadWalletControls, loadChildOrders]);

  const getStatusColor = (status) => {
    const colors = {
      present: '#059669',
      absent: '#DC2626',
      late: '#D97706',
      excused: '#3B82F6'
    };
    return colors[status] || '#A1A1AA';
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading parent portal...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page parent-portal" data-testid="parent-portal">
        <div className="page-header">
          <div>
            <h1>Parent Portal</h1>
            <p>Welcome, {user?.first_name}! View your child's academic progress</p>
          </div>
        </div>

        {children.length === 0 ? (
          <div className="empty-state-card">
            <Student size={48} weight="duotone" />
            <h3>No Children Linked</h3>
            <p>Contact the school administration to link your children to your account</p>
          </div>
        ) : (
          <>
            {/* Child Selector */}
            {children.length > 1 && (
              <div className="child-selector">
                <label>Select Child:</label>
                <div className="child-tabs">
                  {children.map(child => (
                    <button
                      key={child.id}
                      className={`child-tab ${selectedChild?.id === child.id ? 'active' : ''}`}
                      onClick={() => setSelectedChild(child)}
                    >
                      <Student size={18} />
                      <span>{child.name}</span>
                      <span className="class-label">{child.class_name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* View Tabs */}
            <div className="tabs-container">
              <button 
                className={`tab-btn ${view === 'overview' ? 'active' : ''}`}
                onClick={() => setView('overview')}
              >
                <ChartBar size={18} /> Overview
              </button>
              <button 
                className={`tab-btn ${view === 'grades' ? 'active' : ''}`}
                onClick={() => setView('grades')}
              >
                <BookOpen size={18} /> Grades
              </button>
              <button 
                className={`tab-btn ${view === 'assignments' ? 'active' : ''}`}
                onClick={() => setView('assignments')}
              >
                <FileText size={18} /> Assignments
              </button>
              <button 
                className={`tab-btn ${view === 'fees' ? 'active' : ''}`}
                onClick={() => setView('fees')}
              >
                <CurrencyCircleDollar size={18} /> Fees
              </button>
              <button 
                className={`tab-btn ${view === 'attendance' ? 'active' : ''}`}
                onClick={() => setView('attendance')}
              >
                <CheckCircle size={18} /> Attendance
              </button>
              <button 
                className={`tab-btn ${view === 'canteen-wallet' ? 'active' : ''}`}
                onClick={() => setView('canteen-wallet')}
              >
                <QrCode size={18} /> Canteen Wallet
              </button>
              <button
                className={`tab-btn ${view === 'extra-classes' ? 'active' : ''}`}
                onClick={() => setView('extra-classes')}
              >
                <BookOpen size={18} /> Extra Classes
              </button>
              <button
                className={`tab-btn ${view === 'library' ? 'active' : ''}`}
                onClick={() => setView('library')}
              >
                <BookOpen size={18} /> E-Library
              </button>
              <button
                className={`tab-btn ${view === 'ptm' ? 'active' : ''}`}
                onClick={() => setView('ptm')}
              >
                <UsersThree size={18} /> Meet Teachers
              </button>
              <button
                className={`tab-btn ${view === 'chat' ? 'active' : ''}`}
                onClick={() => setView('chat')}
              >
                <ChatDots size={18} /> Chat
              </button>
              <button
                className={`tab-btn ${view === 'services' ? 'active' : ''}`}
                onClick={() => setView('services')}
              >
                <FileText size={18} /> Requests &amp; Services
              </button>
              <button 
                className={`tab-btn ${view === 'security' ? 'active' : ''}`}
                onClick={() => setView('security')}
              >
                <ShieldCheck size={18} /> Security
              </button>
              {childEnrollment?.has_hostel && (
                <button 
                  className={`tab-btn ${view === 'hostel' ? 'active' : ''}`}
                  onClick={() => setView('hostel')}
                >
                  <House size={18} style={{ color: '#8B4513' }} /> Hostel
                </button>
              )}
              {childEnrollment?.has_transport && (
                <button 
                  className={`tab-btn ${view === 'transport' ? 'active' : ''}`}
                  onClick={() => setView('transport')}
                >
                  <MapPin size={18} /> Transport
                </button>
              )}
            </div>

            {/* Canteen Wallet View */}
            {view === 'canteen-wallet' && (
              <div className="wallet-view" style={{ display: 'grid', gap: 16 }}>
                <div className="card" style={{ padding: 20, background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: 'white' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 14, opacity: 0.9 }}>{selectedChild?.name}'s Canteen Wallet</div>
                      <div style={{ fontSize: 32, fontWeight: 700 }}>{formatCurrency(walletSummary?.balance || 0)}</div>
                      {walletControls?.frozen && (
                        <div style={{ marginTop: 6, fontSize: 13, background: 'rgba(255,255,255,0.2)', display: 'inline-block', padding: '4px 10px', borderRadius: 8 }}>
                          Frozen — your child cannot order right now
                        </div>
                      )}
                    </div>
                    <div style={{ padding: 12, borderRadius: '50%', background: 'rgba(255,255,255,0.18)' }}>
                      <Wallet size={28} />
                    </div>
                  </div>
                </div>

                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ marginBottom: 12 }}>Top Up Wallet</h3>
                  <p style={{ color: '#6b7280', marginBottom: 12 }}>Add funds your child can spend at the canteen. Money never goes below zero — no debt, no settling up later.</p>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    {[20, 50, 100, 200].map(amt => (
                      <button key={amt} type="button" className="btn btn-secondary" onClick={() => setTopUpAmount(String(amt))}>
                        GHS {amt}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Amount (GHS)"
                      value={topUpAmount}
                      onChange={(e) => setTopUpAmount(e.target.value)}
                      min="1"
                    />
                    <button className="btn btn-primary" onClick={handleTopUp} disabled={!selectedChild?.id}>
                      Top Up
                    </button>
                  </div>
                </div>

                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ marginBottom: 12 }}>Spending Controls</h3>
                  {!walletControls ? (
                    <div style={{ color: '#6b7280', fontSize: 13 }}>Loading...</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 16 }}>
                      <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                        <span>Freeze wallet (blocks all orders)</span>
                        <input
                          type="checkbox"
                          checked={!!walletControls.frozen}
                          onChange={(e) => setWalletControls(prev => ({ ...prev, frozen: e.target.checked }))}
                        />
                      </label>

                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Daily spending limit (GHS)</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="No limit"
                          value={walletControls.daily_limit ?? ''}
                          onChange={(e) => setWalletControls(prev => ({ ...prev, daily_limit: e.target.value === '' ? null : Number(e.target.value) }))}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Weekly spending limit (GHS)</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="No limit"
                          value={walletControls.weekly_limit ?? ''}
                          onChange={(e) => setWalletControls(prev => ({ ...prev, weekly_limit: e.target.value === '' ? null : Number(e.target.value) }))}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Blocked categories</label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {['breakfast', 'lunch', 'snacks', 'drinks', 'stationery'].map(cat => {
                            const active = (walletControls.blocked_categories || []).includes(cat);
                            return (
                              <button
                                key={cat}
                                type="button"
                                className={active ? 'btn btn-primary' : 'btn btn-secondary'}
                                style={{ textTransform: 'capitalize', padding: '6px 12px' }}
                                onClick={() => toggleBlockedCategory(cat)}
                              >
                                {cat}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <button className="btn btn-primary" onClick={handleSaveControls} disabled={savingControls}>
                        {savingControls ? 'Saving...' : 'Save Controls'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ marginBottom: 12 }}>Recent Orders</h3>
                  {childOrders.length === 0 ? (
                    <div style={{ color: '#6b7280', fontSize: 13 }}>No orders yet.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 10 }}>
                      {childOrders.map(order => (
                        <div key={order.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{order.order_code}</div>
                            <div style={{ color: '#6b7280', fontSize: 13 }}>{order.items.map(l => `${l.quantity}x ${l.name}`).join(', ')}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 700 }}>{formatCurrency(order.total)}</div>
                            <div style={{ color: '#6b7280', fontSize: 12, textTransform: 'capitalize' }}>{order.status}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ marginBottom: 12 }}>Recent wallet activity</h3>
                  {walletHistory.length === 0 ? (
                    <div style={{ color: '#6b7280', fontSize: 13 }}>No activity yet.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 10 }}>
                      {walletHistory.map(entry => (
                        <div key={entry.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{entry.event_type.replace('_', ' ')}</div>
                            <div style={{ color: '#6b7280', fontSize: 13 }}>{entry.description || entry.reference}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 700, color: entry.amount < 0 ? '#dc2626' : '#16a34a' }}>{entry.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(entry.amount))}</div>
                            <div style={{ color: '#6b7280', fontSize: 12 }}>{new Date(entry.created_at).toLocaleString()}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Overview View */}
            {view === 'overview' && childOverview && (
              <div className="parent-overview">
                {/* Student Info Card */}
                <div className="student-info-card">
                  <div className="student-avatar">
                    {childOverview.student.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="student-details">
                    <h2>{childOverview.student.name}</h2>
                    <p>ID: {childOverview.student.student_id}</p>
                    <p>Class: {childOverview.student.class_name || 'Not Assigned'}</p>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="parent-stats-grid">
                  <div className="parent-stat-card" onClick={() => setView('attendance')}>
                    <div className="stat-icon attendance">
                      <CheckCircle size={28} weight="duotone" />
                    </div>
                    <div className="stat-content">
                      <span className="stat-value">{childOverview.attendance.attendance_rate}%</span>
                      <span className="stat-label">Attendance Rate</span>
                      <span className="stat-detail">
                        {childOverview.attendance.present} present, {childOverview.attendance.absent} absent
                      </span>
                    </div>
                    <CaretRight size={20} className="stat-arrow" />
                  </div>

                  <div className="parent-stat-card" onClick={() => setView('fees')}>
                    <div className={`stat-icon ${childOverview.fees.balance > 0 ? 'warning' : 'success'}`}>
                      <CurrencyCircleDollar size={28} weight="duotone" />
                    </div>
                    <div className="stat-content">
                      <span className="stat-value">{formatCurrency(childOverview.fees.balance)}</span>
                      <span className="stat-label">Fee Balance</span>
                      <span className="stat-detail">
                        {childOverview.fees.balance > 0 ? 'Outstanding' : 'Fully Paid'}
                      </span>
                    </div>
                    <CaretRight size={20} className="stat-arrow" />
                  </div>

                  <div className="parent-stat-card" onClick={() => setView('grades')}>
                    <div className="stat-icon grades">
                      <BookOpen size={28} weight="duotone" />
                    </div>
                    <div className="stat-content">
                      <span className="stat-value">{childOverview.academics.total_grades_recorded}</span>
                      <span className="stat-label">Grades Recorded</span>
                      <span className="stat-detail">View detailed report</span>
                    </div>
                    <CaretRight size={20} className="stat-arrow" />
                  </div>
                </div>

                {/* Recent Announcements */}
                {announcements.length > 0 && (
                  <div className="announcements-section">
                    <h3><Megaphone size={20} /> Recent Announcements</h3>
                    <div className="announcement-list">
                      {announcements.slice(0, 3).map(announcement => (
                        <div key={announcement.id} className="announcement-item">
                          <span className={`announcement-type ${announcement.announcement_type}`}>
                            {announcement.announcement_type}
                          </span>
                          <h4>{announcement.title}</h4>
                          <p>{announcement.content.substring(0, 150)}...</p>
                          <span className="announcement-date">
                            {new Date(announcement.publish_date).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Grades View */}
            {view === 'grades' && childGrades && (
              <div className="grades-view">
                {/* Download Report Card */}
                <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <select 
                      value={selectedAcademicTerm || ''} 
                      onChange={(e) => setSelectedAcademicTerm(e.target.value)}
                      style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '100%' }}
                      data-testid="parent-academic-term-select"
                    >
                      {academicTerms.length === 0 && (
                        <option value="" disabled>No terms available</option>
                      )}
                      {academicTerms.map(term => React.createElement(
                        'option',
                        { key: term.id, value: term.id },
                        `${term.term} - ${term.academic_year}`
                      ))}
                    </select>
                  </div>
                  <button 
                    onClick={previewReportCard}
                    disabled={downloadingPDF}
                    className="btn btn-secondary"
                    data-testid="preview-reportcard-parent-btn"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <Eye size={18} /> Preview Report Card
                  </button>
                </div>

                {/* Overall Summary */}
                <div className="overall-grade-card">
                  <div className="overall-header">
                    <h3>Overall Performance</h3>
                  </div>
                  <div className="overall-content">
                    <div 
                      className="overall-grade-badge"
                      style={{ backgroundColor: GRADE_COLORS[childGrades.overall_grade] }}
                    >
                      {childGrades.overall_grade}
                    </div>
                    <div className="overall-details">
                      <span className="overall-percentage">{childGrades.overall_average}%</span>
                      <span className="overall-description">{childGrades.overall_description}</span>
                    </div>
                  </div>
                </div>

                {/* Subject Grades */}
                <div className="subjects-grades">
                  <h3>Subject Performance</h3>
                  {childGrades.subjects.length === 0 ? (
                    <div className="empty-state">No grades recorded yet</div>
                  ) : (
                    <div className="subject-cards">
                      {childGrades.subjects.map(subject => (
                        <div key={subject.subject_name} className="subject-grade-card">
                          <div className="subject-header">
                            <h4>{subject.subject_name}</h4>
                            <span 
                              className="subject-grade"
                              style={{ backgroundColor: GRADE_COLORS[subject.average_grade] }}
                            >
                              {subject.average_grade}
                            </span>
                          </div>
                          <div className="subject-stats">
                            <span className="subject-avg">{subject.average_percentage}%</span>
                            <span className="subject-desc">{subject.average_description}</span>
                          </div>
                          <div className="subject-assessments">
                            {subject.grades.map((grade, idx) => (
                              <div key={idx} className="assessment-item">
                                <span className="assessment-type">{grade.assessment_type.replace('_', ' ')}</span>
                                <span className="assessment-score">{grade.score}/{grade.max_score}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Fees View */}
            {view === 'fees' && childFees && (
              <div className="fees-view" data-testid="parent-fees-view">
                <TermlyFeeCard
                  childFees={childFees}
                  selectedChild={selectedChild}
                  selectedAcademicTerm={selectedAcademicTerm}
                  academicTerms={academicTerms}
                  onPaymentSuccess={handlePaymentSuccess}
                  onTermChange={(termId) => setSelectedAcademicTerm(termId)}
                /> 
              </div>
            )}

            {/* Attendance View */}
            {view === 'attendance' && childAttendance && (
              <div className="attendance-view">
                {/* Academic Term Filter */}
                {academicTerms && academicTerms.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <select 
                      value={selectedAcademicTerm || ''} 
                      onChange={(e) => setSelectedAcademicTerm(e.target.value)}
                      style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '100%', maxWidth: '300px' }}
                      data-testid="parent-attendance-term-filter"
                    >
                      <option value="" disabled={academicTerms.length > 0}>Select Academic Term</option>
                      {academicTerms.map(term => (
                        <option key={term.id} value={term.id}>
                          {term.term} - {term.academic_year}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Summary */}
                <div className="attendance-summary-card">
                  <h3>Last {childAttendance.period_days} Days</h3>
                  <div className="attendance-summary-stats">
                    <div className="att-stat present">
                      <span className="att-value">{childAttendance.summary.present}</span>
                      <span className="att-label">Present</span>
                    </div>
                    <div className="att-stat absent">
                      <span className="att-value">{childAttendance.summary.absent}</span>
                      <span className="att-label">Absent</span>
                    </div>
                    <div className="att-stat late">
                      <span className="att-value">{childAttendance.summary.late}</span>
                      <span className="att-label">Late</span>
                    </div>
                    <div className="att-stat rate">
                      <span className="att-value">{childAttendance.summary.attendance_rate}%</span>
                      <span className="att-label">Rate</span>
                    </div>
                  </div>
                </div>

                {/* Attendance Records */}
                <div className="attendance-records">
                  <h3>Attendance History</h3>
                  {childAttendance.records.length === 0 ? (
                    <div className="empty-state">No attendance records</div>
                  ) : (
                    <div className="records-list">
                      {childAttendance.records.map((record, idx) => (
                        <div key={idx} className="attendance-record">
                          <span className="record-date">
                            {new Date(record.date).toLocaleDateString('en-GB', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short'
                            })}
                          </span>
                          <span 
                            className="record-status"
                            style={{ 
                              backgroundColor: `${getStatusColor(record.status)}20`,
                              color: getStatusColor(record.status)
                            }}
                          >
                            {record.status}
                          </span>
                          {record.remarks && (
                            <span className="record-remarks">{record.remarks}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assignments View */}
            {view === 'assignments' && selectedChild && (
              <div className="assignments-view" data-testid="parent-assignments-view">
                <ParentAssignmentsSection 
                  childId={selectedChild.id} 
                  academicTerms={academicTerms}
                  selectedAcademicTerm={selectedAcademicTerm}
                />
              </div>
            )}

            {/* Extra Classes View */}
            {view === 'extra-classes' && selectedChild && (
              <div className="extra-classes-view" data-testid="parent-extra-classes-view">
                <ParentExtraClassesSection selectedChild={selectedChild} />
              </div>
            )}

            {/* Parent-Teacher Meetings View */}
            {view === 'ptm' && (
              <div className="ptm-view" data-testid="parent-ptm-view">
                <ParentPTMSection kids={children} />
              </div>
            )}

            {/* Chat View */}
            {view === 'chat' && (
              <div className="chat-view" data-testid="parent-chat-view">
                <ParentChat />
              </div>
            )}

            {/* Requests & Services View */}
            {view === 'services' && (
              <div className="services-view" data-testid="parent-services-view">
                <ParentServicesSection kids={children} />
              </div>
            )}

            {/* Hostel View */}
            {view === 'hostel' && selectedChild && (
              <div className="hostel-view" data-testid="parent-hostel-view">
                <ParentHostelSection 
                  data={childHostelData}
                  childName={selectedChild.name || [selectedChild.first_name, selectedChild.last_name].filter(Boolean).join(' ') || 'your child'}
                />
              </div>
            )}

            {/* Transport View */}
            {view === 'transport' && selectedChild && (
              <div className="transport-view" data-testid="parent-transport-view">
                <ParentTransportSection 
                  data={childTransportData}
                  childName={selectedChild.name || [selectedChild.first_name, selectedChild.last_name].filter(Boolean).join(' ') || 'your child'}
                />
              </div>
            )}

            {/* Security View */}
            {view === 'security' && (
              <div className="security-view" data-testid="parent-security-view">
                <ParentSecurityPortalPage />
              </div>
            )}

            {/* E-Library View */}
            {view === 'library' && selectedChild && (
              <div className="library-view" data-testid="parent-library-view">
                <ParentLibrarySection
                  childId={selectedChild.id}
                  childName={selectedChild.name}
                />
              </div>
            )}
          </>
        )}

        {/* Report Card Preview Modal */}
        <ReportCardPreviewModal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          html={previewHTML}
          studentName={selectedChild?.first_name || 'Child'}
          academicTerm={selectedAcademicTerm}
          studentId={selectedChild?.id}
          onDownloadComplete={() => toast.success('Report card downloaded successfully')}
        />
      </div>
    </Layout>
  );
}

export default ParentPortalPage;
